/* Knowledge base — handover §14 step 11, and one of the two mechanisms the
   README names as what fights the thing that will actually kill this.

   That thing is not missing features, it is unmaintained content: "a register
   that is 60% accurate is worse than none, because people stop checking and
   start guessing." An article nobody has looked at in two years is exactly that
   — still confidently telling you how to do something that changed.

   So the review flag is not a badge tucked in a corner. Articles past their
   review interval sort to the top, get their own lens and their own stat, and
   marking one reviewed is a single button on the row. The same flag already
   feeds the expiry aggregation alongside warranties and contracts, deliberately
   — a stale runbook is a thing falling due, not a separate kind of problem. */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useOnline } from '../../hooks/useOnline';
import { daysSince, fmtDate } from '../../lib/format';
import { REVIEW_DAYS } from '../../services/mockData';
import type { IArticle } from '../../models';
import { qk } from '../../query/keys';
import { useServices } from '../../services/context';
import {
  Badge,
  Button,
  Controls,
  Drawer,
  EmptyState,
  LoadError,
  Panel,
  SearchBox,
  Segmented,
  StatCard,
  StatGrid,
  TableSkeleton,
} from '../ui';
import s from './screens.module.scss';

/** Days until this article is due a review. Negative means overdue. */
export function reviewIn(article: Pick<IArticle, 'upd'>): number {
  return REVIEW_DAYS - daysSince(article.upd);
}

export function KnowledgeBaseScreen() {
  const svc = useServices();
  const online = useOnline();
  const qc = useQueryClient();

  const [query, setQuery] = useState('');
  const [lens, setLens] = useState('all');
  const [selected, setSelected] = useState<IArticle | null>(null);
  const [draft, setDraft] = useState<IArticle | null>(null);

  const articles = useQuery({ queryKey: qk.articles, queryFn: () => svc.articles.list() });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: qk.articles });
    void qc.invalidateQueries({ queryKey: qk.audit });
    setSelected(null);
    setDraft(null);
  };

  const markReviewed = useMutation({

    meta: { ok: 'Marked as reviewed' },
    mutationFn: (id: string) => svc.articles.markReviewed(id),
    onSuccess: refresh,
  });

  const save = useMutation({

    meta: { ok: 'Article saved' },
    mutationFn: (article: IArticle) => svc.articles.save(article),
    onSuccess: refresh,
  });

  const categories = useMemo(
    () => [...new Set((articles.data ?? []).map((a) => a.cat))].sort(),
    [articles.data],
  );

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (articles.data ?? [])
      .filter((a) => {
        if (lens === 'due' && reviewIn(a) > 0) return false;
        if (lens !== 'all' && lens !== 'due' && a.cat !== lens) return false;
        if (!q) return true;
        return (
          a.title.toLowerCase().includes(q) ||
          a.desc.toLowerCase().includes(q) ||
          a.steps.some((step) => step.toLowerCase().includes(q))
        );
      })
      /* Overdue first. A knowledge base sorted alphabetically hides the stale
         ones among the current ones, which is how they stay stale. */
      .sort((a, b) => reviewIn(a) - reviewIn(b));
  }, [articles.data, lens, query]);

  const overdue = (articles.data ?? []).filter((a) => reviewIn(a) <= 0).length;
  const soon = (articles.data ?? []).filter((a) => reviewIn(a) > 0 && reviewIn(a) <= 30).length;

  const blank = (): IArticle => ({
    id: '',
    cat: categories[0] ?? 'General',
    title: '',
    icon: 'ti-book-2',
    desc: '',
    upd: new Date().toISOString().slice(0, 10),
    by: '',
    steps: [''],
  });

  return (
    <>
      <div className={s.head}>
        <div className={s.grow}>
          <h1>Knowledge base</h1>
          <p>
            Runbooks and how-tos, oldest review first. Anything past {REVIEW_DAYS} days is
            flagged here and in the expiry list.
          </p>
        </div>
        <Button primary icon="ti-plus" onClick={() => setDraft(blank())} disabled={!online}>
          New article
        </Button>
      </div>

      {articles.isError && <LoadError error={articles.error} what="the knowledge base" />}

      <StatGrid>
        <StatCard
          icon="ti-book-2"
          label="Articles"
          value={articles.isLoading ? '—' : (articles.data?.length ?? 0)}
          tone="info"
          foot={`${categories.length} categories`}
        />
        <StatCard
          icon="ti-alert-triangle"
          label="Overdue a review"
          value={articles.isLoading ? '—' : overdue}
          tone={overdue > 0 ? 'bad' : 'ok'}
          foot={overdue > 0 ? 'Assume these are wrong' : 'All current'}
        />
        <StatCard
          icon="ti-clock"
          label="Due within 30 days"
          value={articles.isLoading ? '—' : soon}
          tone={soon > 0 ? 'warn' : 'ok'}
          foot={`Reviewed every ${REVIEW_DAYS} days`}
        />
      </StatGrid>

      <Controls count={`${rows.length} of ${articles.data?.length ?? 0}`}>
        <SearchBox value={query} onChange={setQuery} placeholder="Title, description or a step…" />
        <Segmented
          label="Lens"
          value={lens}
          onChange={setLens}
          options={[
            { value: 'all', label: 'All' },
            { value: 'due', label: 'Needs review' },
            ...categories.map((c) => ({ value: c, label: c })),
          ]}
        />
      </Controls>

      <Panel flush>
        {articles.isLoading ? (
          <TableSkeleton />
        ) : rows.length === 0 ? (
          <EmptyState icon="ti-book-2" title="Nothing here yet">
            Runbooks and how-tos, with a review date on each.
          </EmptyState>
        ) : (
          <div className={s.rows}>
            {rows.map((a) => {
              const days = reviewIn(a);
              return (
                <div key={a.id} className={s.row}>
                  <span className={s.rowIcon}>
                    <i className={`ti ${a.icon || 'ti-book-2'}`} aria-hidden />
                  </span>
                  <button
                    type="button"
                    className={`${s.rowMain} ${s.rowButton}`}
                    onClick={() => setSelected(a)}
                  >
                    <div className={s.rowTitle}>{a.title}</div>
                    <div className={s.rowSub}>
                      {a.cat} · {a.steps.length} steps · updated {fmtDate(a.upd)} by {a.by || '—'}
                    </div>
                  </button>
                  <Badge tone={days <= 0 ? 'bad' : days <= 30 ? 'warn' : 'ok'}>
                    {days <= 0 ? `${Math.abs(days)} days overdue` : `Review in ${days} days`}
                  </Badge>
                  <Button
                    icon="ti-check"
                    disabled={!online || markReviewed.isPending}
                    onClick={() => markReviewed.mutate(a.id)}
                    title="Records that you have read this today and it is still right"
                  >
                    Reviewed
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </Panel>

      {/* Reading an article. Separate from editing because the common case by
          far is following the steps, not changing them. */}
      <Drawer
        open={selected !== null}
        title={selected?.title ?? ''}
        sub={selected?.desc}
        avatar={<i className={`ti ${selected?.icon ?? 'ti-book-2'}`} aria-hidden />}
        meta={
          selected && (
            <>
              <Badge tone="muted">{selected.cat}</Badge>
              <Badge tone={reviewIn(selected) <= 0 ? 'bad' : 'ok'}>
                Updated {fmtDate(selected.upd)}
              </Badge>
            </>
          )
        }
        onClose={() => setSelected(null)}
      >
        {selected && (
          <>
            <ol className={s.steps}>
              {selected.steps.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>

            <div className={s.actions}>
              <Button
                primary
                icon="ti-check"
                disabled={!online || markReviewed.isPending}
                onClick={() => markReviewed.mutate(selected.id)}
              >
                Still correct — mark reviewed
              </Button>
              <Button icon="ti-pencil" disabled={!online} onClick={() => setDraft({ ...selected })}>
                Edit
              </Button>
            </div>
          </>
        )}
      </Drawer>

      {/* Editing. Steps are a list, so they get the same treatment as any other
          nested list rather than a textarea somebody has to split by hand. */}
      <Drawer
        open={draft !== null}
        title={draft?.id ? 'Edit article' : 'New article'}
        sub={draft?.id ? draft.title : undefined}
        avatar={<i className={`ti ${draft?.icon ?? 'ti-book-2'}`} aria-hidden />}
        onClose={() => setDraft(null)}
      >
        {draft && (
          <>
            <div className={s.field}>
              <label htmlFor="kb-title">Title</label>
              <input
                id="kb-title"
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              />
            </div>
            <div className={s.field}>
              <label htmlFor="kb-cat">Category</label>
              <input
                id="kb-cat"
                value={draft.cat}
                onChange={(e) => setDraft({ ...draft, cat: e.target.value })}
                list="kb-categories"
              />
              <datalist id="kb-categories">
                {categories.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </div>
            <div className={s.field}>
              <label htmlFor="kb-desc">Description</label>
              <textarea
                id="kb-desc"
                value={draft.desc}
                onChange={(e) => setDraft({ ...draft, desc: e.target.value })}
              />
            </div>
            <div className={s.field}>
              <label htmlFor="kb-icon">Icon</label>
              <input
                id="kb-icon"
                value={draft.icon}
                onChange={(e) => setDraft({ ...draft, icon: e.target.value })}
              />
            </div>
            <div className={s.field}>
              <label htmlFor="kb-by">Written by</label>
              <input
                id="kb-by"
                value={draft.by}
                onChange={(e) => setDraft({ ...draft, by: e.target.value })}
              />
            </div>

            <div>
              <div className={s.sectionLabel}>Steps</div>
 <div className={`${s.stack} ${s.rowGap}`} >
                {draft.steps.map((step, i) => (
                  <div key={i} className={s.fieldRowBody}>
                    <input
                      value={step}
                      aria-label={`Step ${i + 1}`}
                      placeholder={`Step ${i + 1}`}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          steps: draft.steps.map((x, n) => (n === i ? e.target.value : x)),
                        })
                      }
                    />
                    <button
                      type="button"
                      className={s.iconOnly}
                      aria-label={`Remove step ${i + 1}`}
                      disabled={draft.steps.length === 1}
                      onClick={() =>
                        setDraft({ ...draft, steps: draft.steps.filter((_, n) => n !== i) })
                      }
                    >
                      <i className="ti ti-trash" aria-hidden />
                    </button>
                  </div>
                ))}
              </div>
              <div className={s.spaced}>
                <Button
                  icon="ti-plus"
                  onClick={() => setDraft({ ...draft, steps: [...draft.steps, ''] })}
                >
                  Add step
                </Button>
              </div>
            </div>

            <div className={s.ownedNote}>
              <i className="ti ti-calendar-check" aria-hidden />
              <span>
                Saving sets the review clock back to today. If you are correcting a typo rather
                than checking the whole thing is still right, "mark reviewed" is the honest button
                — the date is only worth anything if it means somebody actually read it.
              </span>
            </div>

            <div className={s.actions}>
              <Button
                primary
                icon="ti-device-floppy"
                disabled={!online || save.isPending || draft.title.trim() === ''}
                onClick={() =>
                  save.mutate({ ...draft, steps: draft.steps.filter((x) => x.trim() !== '') })
                }
              >
                {save.isPending ? 'Saving…' : 'Save article'}
              </Button>
            </div>
          </>
        )}
      </Drawer>
    </>
  );
}
